import { cn } from "@/lib/utils/cn";

/**
 * A back-office status pill: soft tint, matching border, a coloured dot.
 *
 * One shape for one idea, differing only in tone — the SPA counterpart of the
 * Django `.status-chip` family. Text is the tone's `-ink`, a darker step of the
 * same hue: the tone token itself dropped under 4.5:1 on its own tint. Solid fills would make a column of unrelated
 * looking things. Shared by the financial-aid and tool admin lists, which map
 * their own status codes to a tone and supply their own localized label.
 */
export type StatusTone = "success" | "warning" | "muted" | "destructive" | "info";

const TONES: Record<StatusTone, { chip: string; dot: string }> = {
  success: {
    chip: "border-success/30 bg-success/10 text-success-ink",
    dot: "bg-success",
  },
  warning: {
    chip: "border-warning/30 bg-warning/10 text-warning-ink",
    dot: "bg-warning",
  },
  muted: {
    chip: "border-border bg-muted text-muted-foreground",
    dot: "bg-muted-foreground",
  },
  destructive: {
    chip: "border-destructive/30 bg-destructive/10 text-destructive-ink",
    dot: "bg-destructive",
  },
  info: {
    chip: "border-info/30 bg-info/10 text-info-ink",
    dot: "bg-info",
  },
};

export function StatusPill({
  tone,
  children,
}: {
  tone: StatusTone;
  children: React.ReactNode;
}) {
  const style = TONES[tone] ?? TONES.muted;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        style.chip,
      )}
    >
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", style.dot)} />
      {children}
    </span>
  );
}
